import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X, FileText } from 'lucide-react';
type Status = 'Completed' | 'No Content' | 'Workers Signing';
interface Record {
  id: string;
  fileType: 'pdf' | 'missing';
  fileName: string;
  site: string;
  subName: string;
  date: string;
  status: Status;
}
const SITES = ['1760 3rd Ave', '1760 Jerome Ave', 'Court Square LIC', '99 Claremont Ave', '251 Douglass Street', '450 Park Avenue South', '520 Fifth Avenue'];
const SUBS = ['Aurora Paint Corp.', 'Skytrac', 'Flushing Iron Weld', 'Sector Security', 'Luxintec', 'V10 Construction', 'Kitchens Of USA', 'Otis Elevator Company', 'Sunkeeper Solar', 'Architectural Flooring Resource Inc'];
function generateMockData(): Record[] {
  const data: Record[] = [];
  let id = 1;
  const add = (date: string, status: Status, subIdx: number, siteIdx: number) => {
    const isCompleted = status === 'Completed';
    data.push({
      id: String(id++),
      fileType: isCompleted ? 'pdf' : status === 'Workers Signing' ? 'pdf' : 'missing',
      fileName: isCompleted ? 'Signed Toolbox Talk' : '',
      site: SITES[siteIdx % SITES.length],
      subName: SUBS[subIdx % SUBS.length],
      date,
      status
    });
  };
  // This week (03/09 - 03/15)
  for (let i = 0; i < 15; i++) add('03/14/2026', 'No Content', i, i);
  for (let i = 0; i < 10; i++) add('03/12/2026', 'Workers Signing', i + 2, i + 1);
  for (let i = 0; i < 20; i++) add('03/10/2026', 'Completed', i + 4, i + 2);
  // Previous week (03/02 - 03/08)
  for (let i = 0; i < 8; i++) add('03/05/2026', 'No Content', i + 1, i + 3);
  for (let i = 0; i < 5; i++) add('03/04/2026', 'Workers Signing', i + 3, i + 4);
  for (let i = 0; i < 25; i++) add('03/03/2026', 'Completed', i + 5, i + 5);
  return data;
}
const MOCK_DATA = generateMockData();
export function ToolboxTalkPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [periodLabel, setPeriodLabel] = useState<string | null>(null);
  const [siteFilter, setSiteFilter] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{
    start: string | null;
    end: string | null;
  }>({
    start: null,
    end: null
  });
  useEffect(() => {
    const period = searchParams.get('period');
    const status = searchParams.get('status');
    const site = searchParams.get('site');
    if (status === 'incomplete') {
      setStatusFilter('Incomplete');
    } else {
      setStatusFilter(null);
    }
    if (site) {
      setSiteFilter(site);
    } else {
      setSiteFilter(null);
    }
    if (period === 'thisweek') {
      setPeriodLabel('This week');
      setDateRange({
        start: '03/09/2026',
        end: '03/15/2026'
      });
    } else if (period === 'previousweek') {
      setPeriodLabel('Previous week');
      setDateRange({
        start: '03/02/2026',
        end: '03/08/2026'
      });
    } else {
      setPeriodLabel(null);
      setDateRange({
        start: null,
        end: null
      });
    }
  }, [searchParams]);
  const clearAllFilters = () => {
    setStatusFilter(null);
    setPeriodLabel(null);
    setSiteFilter(null);
    setDateRange({
      start: null,
      end: null
    });
    setSearchParams({});
  };
  const removeFilter = (key: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(key);
    setSearchParams(newParams);
  };
  const filteredData = useMemo(() => {
    return MOCK_DATA.filter(item => {
      if (searchQuery && !item.subName.toLowerCase().includes(searchQuery.toLowerCase()) && !item.site.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (statusFilter === 'Incomplete') {
        if (item.status === 'Completed') return false;
      } else if (statusFilter && item.status !== statusFilter) {
        return false;
      }
      if (siteFilter && item.site !== siteFilter) {
        return false;
      }
      if (dateRange.start && item.date < dateRange.start) return false;
      if (dateRange.end && item.date > dateRange.end) return false;
      return true;
    });
  }, [searchQuery, statusFilter, dateRange, siteFilter]);
  const hasActiveFilters = statusFilter !== null || dateRange.start !== null || siteFilter !== null;
  return <div className="min-h-screen bg-[#fafafa] font-sans flex flex-col" data-id="element-4466" data-mp="23aviqyqopajd">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4" data-id="element-4467" data-mp="2i9h5su30ksgr">
        <div className="flex items-center gap-2" data-id="element-4468" data-mp="2da4cbqhn0n57">
          <div className="text-blue-600 font-bold text-2xl tracking-tighter flex items-center" data-id="element-4469" data-mp="2damef3qzdvl1">
            CHST
            <div className="w-6 h-6 border-4 border-blue-600 rounded-full mx-0.5 relative" data-id="element-4470" data-mp="nx0tbbjmki3g">
              <div className="absolute inset-0 m-auto w-2 h-2 bg-blue-600 rounded-full" data-id="element-4471" data-mp="ixo96yk51p4k"></div>
            </div>
          </div>
          <div className="text-[10px] font-bold text-blue-600 tracking-widest absolute mt-8 ml-1" data-id="element-4472" data-mp="2zi9yfu35an17">
            L D E R S
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 ml-4" data-id="element-4473" data-mp="2kskl70hs2skk">Toolbox Talk</h1>
      </div>

      <main className="flex-1 p-6 max-w-[1600px] mx-auto w-full" data-id="element-4474" data-mp="2prf402fqwh4o">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-4475" data-mp="2bsxgo5tr56na">
          <h2 className="text-xl text-gray-700" data-id="element-4476" data-mp="2fu0yuyz9vero">
            Found{' '}
            <span className="font-semibold text-gray-900" data-id="element-4477" data-mp="2fs6dxko03mnl">
              {filteredData.length}
            </span>{' '}
            Toolbox Talks
          </h2>

          <div className="flex items-center gap-3" data-id="element-4478" data-mp="26unom43bqm5w">
            <div className="relative" data-id="element-4479" data-mp="34yaglojo07">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-4480" data-mp="46c2su2yolm" />
              <input type="text" placeholder="Search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4481" data-mp="4zved3xhijlm" />
            </div>
            <button className="relative flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 bg-white transition-colors shadow-sm" data-id="element-4482" data-mp="3kva9k9j551bf">
              <SlidersHorizontal size={16} data-id="element-4483" data-mp="hejz9igj023x" />
              Filters
              {hasActiveFilters && <span className="absolute top-0 right-0 -mt-1 -mr-1 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" data-id="element-4484" data-mp="cjnx3phkryd3"></span>}
            </button>
          </div>
        </div>

        {hasActiveFilters && <div className="flex items-center flex-wrap gap-2 mb-6" data-id="element-4485" data-mp="10at7hp4qxyg0">
            {siteFilter && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white rounded-full text-sm" data-id="element-4486" data-mp="18kh35n8ys7qe">
                <span className="text-gray-300" data-id="element-4487" data-mp="1cmlw0jrl90bz">Site:</span>
                <span className="font-medium" data-id="element-4488" data-mp="1hknep3l3qxmx">{siteFilter}</span>
                <button onClick={() => removeFilter('site')} className="ml-1 text-gray-400 hover:text-white transition-colors" data-id="element-4489" data-mp="1hj2m3jiclorp">
                  <X size={14} data-id="element-4490" data-mp="1ri1ur2345kep" />
                </button>
              </div>}

            {statusFilter && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white rounded-full text-sm" data-id="element-4491" data-mp="1vilso7vigs1w">
                <span className="text-gray-300" data-id="element-4492" data-mp="20dhjj4p0h9df">Status:</span>
                <span className="font-medium" data-id="element-4493" data-mp="258ww7tba8z89">{statusFilter}</span>
                <button onClick={() => removeFilter('status')} className="ml-1 text-gray-400 hover:text-white transition-colors" data-id="element-4494" data-mp="2a9uoiyq0b9nh">
                  <X size={14} data-id="element-4495" data-mp="30jchzz5q9pg9" />
                </button>
              </div>}

            {periodLabel && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white rounded-full text-sm" data-id="element-4496" data-mp="2vksys6iiqrrl">
                <span className="text-gray-300" data-id="element-4497" data-mp="39izmwuxo1de2">Period:</span>
                <span className="font-medium" data-id="element-4498" data-mp="34ky0c0116vxu">{periodLabel}</span>
                <span className="text-gray-400 text-xs" data-id="element-4499" data-mp="34lqzofxnuj5y">
                  ({dateRange.start} – {dateRange.end})
                </span>
                <button onClick={() => removeFilter('period')} className="ml-1 text-gray-400 hover:text-white transition-colors" data-id="element-4500" data-mp="2cu1pf4yj71d7">
                  <X size={14} data-id="element-4501" data-mp="2qn179dlsd7nx" />
                </button>
              </div>}

            <button onClick={clearAllFilters} className="text-sm text-gray-500 hover:text-gray-900 underline underline-offset-2 ml-2 transition-colors" data-id="element-4502" data-mp="2lsdgf83b8166">
              Clear all
            </button>
          </div>}

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-4503" data-mp="8mylmro7uyjq">
          <div className="overflow-x-auto" data-id="element-4504" data-mp="df9x8rsbk8s4">
            <table className="w-full text-left text-sm" data-id="element-4505" data-mp="dgbbf54mtbe2">
              <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 font-semibold text-xs tracking-wider" data-id="element-4506" data-mp="3vtr5o34j08di">
                <tr data-id="element-4507" data-mp="4merzy2uqd2c">
                  <th className="px-6 py-4" data-id="element-4508" data-mp="sbr87hwljle5">FILE</th>
                  <th className="px-6 py-4" data-id="element-4509" data-mp="sd2999ynqf1k">SITE</th>
                  <th className="px-6 py-4" data-id="element-4510" data-mp="wdciqwlnrnl6">SUB NAME</th>
                  <th className="px-6 py-4" data-id="element-4511" data-mp="wcsyvacg6p4i">DATE</th>
                  <th className="px-6 py-4" data-id="element-4512" data-mp="ie307q9zche1">STATUS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-4513" data-mp="ncecs8rnt5zy">
                {filteredData.map(row => <tr key={row.id} className="hover:bg-gray-50 transition-colors" data-id="element-4514" data-mp="333963c30gp4u">
                    <td className="px-6 py-4" data-id="element-4515" data-mp="3846nhmg75f5a">
                      <div className="flex items-center gap-3" data-id="element-4516" data-mp="2qte4bf7q1mrk">
                        {row.fileType === 'pdf' ? <div className="relative" data-id="element-4517" data-mp="2vsijcmhbuept">
                            <FileText size={24} className="text-gray-300" data-id="element-4518" data-mp="2vu1vc0vya3ls" />
                            <div className="absolute bottom-0 left-0 bg-red-600 text-white text-[8px] font-bold px-1 rounded-sm" data-id="element-4519" data-mp="2h32r1225e180">
                              PDF
                            </div>
                          </div> : <div className="relative" data-id="element-4520" data-mp="2c4i2w10677zx">
                            <FileText size={24} className="text-gray-200" data-id="element-4521" data-mp="2c25bkx8ivla7" />
                            <div className="absolute bottom-0 left-0 bg-gray-500 text-white text-[8px] font-bold px-1 rounded-sm" data-id="element-4522" data-mp="1y61k3bp19r1f">
                              ?
                            </div>
                          </div>}
                        <span className="text-gray-900" data-id="element-4523" data-mp="1byeqv465b6x">{row.fileName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-900" data-id="element-4524" data-mp="1lval1qjyfe6p">{row.site}</td>
                    <td className="px-6 py-4 text-gray-900" data-id="element-4525" data-mp="1gvo7th44lxxw">{row.subName}</td>
                    <td className="px-6 py-4 text-gray-900" data-id="element-4526" data-mp="1vk28ble2laba">{row.date}</td>
                    <td className="px-6 py-4" data-id="element-4527" data-mp="1vldd9oj94o3z">
                      {row.status === 'Completed' && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-emerald-100 text-emerald-700" data-id="element-4528" data-mp="1qr93kxilq1z7">
                          Completed
                        </span>}
                      {row.status === 'No Content' && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700" data-id="element-4529" data-mp="2ei536gtau1h0">
                          No Content
                        </span>}
                      {row.status === 'Workers Signing' && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-700" data-id="element-4530" data-mp="f73wbfhca1n6">
                          Workers Signing
                        </span>}
                    </td>
                  </tr>)}
                {filteredData.length === 0 && <tr data-id="element-4531" data-mp="1itplocsmzonb">
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500" data-id="element-4532" data-mp="1itzcu3pf41ul">
                      No Toolbox Talks found matching your criteria.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>;
}